import { RefreshCw, RotateCcw, ScrollText } from "lucide-react";
import * as React from "react";
import { Toolbar } from "@/components/layout/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Callout, EmptyState, ErrorState, Spinner, TableSkeleton } from "@/components/ui/feedback";
import { CopyButton } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { CallPager, CallRangeLabel } from "@/features/call-report";
import { ApiError } from "@/lib/api";
import {
  DELIVERIES_PAGE_SIZE, useReplayWebhookDelivery, useWebhookDeliveries, useWebhookDelivery,
  useWebhookEventTypes, type WebhookDeliveryFilters,
} from "@/lib/queries";
import type { WebhookEndpoint } from "@/lib/types";
import { formatDateTime } from "@/lib/utils";
import {
  DELIVERY_STATUSES, deliveryStatusText, eventTerm, eventTypeOrder, isSettled, lastAnswer, prettyJson,
} from "@/lib/webhooks";

const ALL = "all";

/**
 * The delivery log (ADR-022): what was sent, where, and what the receiver said. One endpoint's on its
 * page; every endpoint's, with a filter by endpoint, on the webhooks page.
 *
 * A page of the log carries no events. They hold names and numbers, so one is read only when its
 * delivery is opened. The log polls while something on the page is still queued, so a test's answer
 * shows up on its own.
 */
export function WebhookDeliveriesCard({
  endpointId, endpoints = [],
}: {
  /** One endpoint's log. Without it, every endpoint's. */
  endpointId?: number;
  /** For the endpoint column and filter, when the log is every endpoint's. */
  endpoints?: readonly WebhookEndpoint[];
}) {
  const [status, setStatus] = React.useState(ALL);
  const [eventType, setEventType] = React.useState(ALL);
  const [kind, setKind] = React.useState(ALL);
  const [endpoint, setEndpoint] = React.useState(ALL);
  const [page, setPage] = React.useState(0);
  const [opened, setOpened] = React.useState<number>();
  const eventTypes = useWebhookEventTypes();
  const everyEndpoint = endpointId === undefined;

  const filters: WebhookDeliveryFilters = {
    endpoint_id: endpointId ?? (endpoint === ALL ? undefined : Number(endpoint)),
    status: status === ALL ? undefined : status,
    event_type: eventType === ALL ? undefined : eventType,
    test: kind === ALL ? undefined : kind === "tests",
  };
  const { data, isPending, isFetching, error, refetch } = useWebhookDeliveries(filters, page);
  const names = new Map(endpoints.map((e) => [e.id, e.name]));
  const filtered = status !== ALL || eventType !== ALL || kind !== ALL || endpoint !== ALL;
  const openedRow = data?.items.find((d) => d.id === opened);

  const choose = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setPage(0);
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader
        title="Deliveries"
        description="What was sent, and what the receiver answered. Click one to read the event and send it again."
        action={
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Refresh the deliveries"
            disabled={isFetching}
            onClick={() => void refetch()}
          >
            <RefreshCw />
          </Button>
        }
      />
      <Toolbar>
        {everyEndpoint && endpoints.length > 0 && (
          <Select value={endpoint} onValueChange={choose(setEndpoint)}>
            <SelectTrigger className="w-52" aria-label="Endpoint">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Every endpoint</SelectItem>
              {endpoints.map((e) => (
                <SelectItem key={e.id} value={String(e.id)}>
                  {e.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select value={status} onValueChange={choose(setStatus)}>
          <SelectTrigger className="w-40" aria-label="Status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Every status</SelectItem>
            {DELIVERY_STATUSES.map((s) => (
              <SelectItem key={s} value={s} description={deliveryStatusText(s).description}>
                {deliveryStatusText(s).label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={eventType} onValueChange={choose(setEventType)}>
          <SelectTrigger className="w-56" aria-label="Event">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Every event</SelectItem>
            {eventTypeOrder((eventTypes.data ?? []).map((t) => t.type)).map((type) => (
              <SelectItem key={type} value={type}>
                {eventTerm(type).label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={kind} onValueChange={choose(setKind)}>
          <SelectTrigger className="w-40" aria-label="Events or tests">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Events and tests</SelectItem>
            <SelectItem value="events">Events only</SelectItem>
            <SelectItem value="tests">Tests only</SelectItem>
          </SelectContent>
        </Select>
        <span className="ml-auto text-xs text-text-muted">
          {data && (
            <CallRangeLabel from={page * DELIVERIES_PAGE_SIZE} count={data.items.length} total={data.total} />
          )}
        </span>
      </Toolbar>

      {isPending ? (
        <TableSkeleton cols={everyEndpoint ? 6 : 5} />
      ) : error ? (
        <ErrorState
          error={error}
          title="Could not load the deliveries"
          action={
            <Button variant="outline" onClick={() => void refetch()}>
              Try again
            </Button>
          }
        />
      ) : data.items.length === 0 ? (
        <EmptyState
          icon={ScrollText}
          title={filtered ? "No deliveries match" : "Nothing sent yet"}
          description={
            filtered
              ? "Try another filter."
              : "When an event this endpoint hears happens, or a test is sent, it shows up here."
          }
        />
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Queued</TableHead>
                <TableHead>Event</TableHead>
                {everyEndpoint && <TableHead>Endpoint</TableHead>}
                <TableHead>Status</TableHead>
                <TableHead>Last answer</TableHead>
                <TableHead className="text-right">Tries</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((delivery) => {
                const state = deliveryStatusText(delivery.status);
                const answer = lastAnswer(delivery);
                return (
                  <TableRow
                    key={delivery.id}
                    interactive
                    tabIndex={0}
                    onClick={() => setOpened(delivery.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setOpened(delivery.id);
                      }
                    }}
                  >
                    <TableCell className="whitespace-nowrap text-xs text-text-muted">
                      {formatDateTime(delivery.created_at)}
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1.5 text-sm text-text">
                        {eventTerm(delivery.event_type).label}
                        {delivery.test && (
                          <Badge tone="accent" size="sm">
                            Test
                          </Badge>
                        )}
                      </span>
                    </TableCell>
                    {everyEndpoint && (
                      <TableCell className="text-xs text-text">
                        {names.get(delivery.endpoint_id) ?? `Endpoint ${delivery.endpoint_id}`}
                      </TableCell>
                    )}
                    <TableCell>
                      <Badge tone={state.tone} size="sm" title={state.description}>
                        {delivery.status === "pending" && <Spinner className="size-2.5" />}
                        {state.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-xs truncate text-xs text-text-muted" title={answer}>
                      {answer}
                    </TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{delivery.attempts}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <CallPager
            page={page}
            count={data.items.length}
            total={data.total}
            onChange={setPage}
            busy={isFetching}
            pageSize={DELIVERIES_PAGE_SIZE}
          />
        </>
      )}

      <WebhookDeliveryDrawer
        deliveryId={opened}
        onClose={() => setOpened(undefined)}
        endpointName={everyEndpoint && openedRow ? names.get(openedRow.endpoint_id) : undefined}
      />
    </Card>
  );
}

/**
 * One delivery: where it stands, what the receiver said, and the event it carries. Sending it again
 * is for a settled delivery that still has its event, to the endpoint as it now stands; core-api
 * refuses the rest and says why.
 */
export function WebhookDeliveryDrawer({
  deliveryId, onClose, endpointName,
}: {
  deliveryId: number | undefined;
  onClose: () => void;
  endpointName?: string;
}) {
  const toast = useToast();
  const { data, isPending, error, refetch } = useWebhookDelivery(deliveryId);
  const replay = useReplayWebhookDelivery();
  const state = data ? deliveryStatusText(data.status) : null;
  const asked = data?.replay_requested_at ?? null;
  const sendingAgain = Boolean(
    data && asked !== null && (data.last_attempt_at === null || asked > data.last_attempt_at),
  );
  const canSendAgain = Boolean(data && isSettled(data.status) && data.payload && !sendingAgain);

  const sendAgain = async () => {
    if (!data) return;
    try {
      await replay.mutateAsync(data.id);
      toast.success("Sending it again", eventTerm(data.event_type).label);
    } catch (e) {
      toast.error("Could not send it again", e instanceof ApiError ? e.message : "Please try again.");
    }
  };

  return (
    <Dialog open={deliveryId !== undefined} onOpenChange={(open) => !open && onClose()}>
      <DialogContent variant="drawer" size="lg">
        <DialogHeader>
          <DialogTitle>{data ? eventTerm(data.event_type).label : "Delivery"}</DialogTitle>
          <DialogDescription>
            {data
              ? `${data.test ? "A test" : "An event"} queued ${formatDateTime(data.created_at)}${endpointName ? ` for ${endpointName}` : ""}.`
              : "Reading the delivery…"}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {isPending ? (
            <div className="flex min-h-40 items-center justify-center">
              <Spinner className="size-5" />
            </div>
          ) : error ? (
            <ErrorState
              error={error}
              title="Could not read the delivery"
              action={
                <Button variant="outline" onClick={() => void refetch()}>
                  Try again
                </Button>
              }
            />
          ) : data && state ? (
            <>
              <div className="space-y-1.5">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={sendingAgain ? "neutral" : state.tone}>
                    {(data.status === "pending" || sendingAgain) && <Spinner className="size-2.5" />}
                    {sendingAgain ? "Sending again" : state.label}
                  </Badge>
                  {data.test && <Badge tone="accent">Test</Badge>}
                </div>
                <p className="text-xs text-text-muted">
                  {sendingAgain
                    ? "Asked to be sent again. The webhooks service sends it within a few seconds."
                    : state.description}
                </p>
              </div>
              <dl className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-2 text-xs">
                <dt className="text-text-muted">Last answer</dt>
                <dd className="break-words text-text">{lastAnswer(data)}</dd>
                <dt className="text-text-muted">Tries</dt>
                <dd className="tabular-nums text-text">{data.attempts}</dd>
                <dt className="text-text-muted">Last try</dt>
                <dd className="text-text">{data.last_attempt_at ? formatDateTime(data.last_attempt_at) : "—"}</dd>
                {data.status === "failed" && data.next_attempt_at && (
                  <>
                    <dt className="text-text-muted">Next try</dt>
                    <dd className="text-text">{formatDateTime(data.next_attempt_at)}</dd>
                  </>
                )}
                {data.delivered_at && (
                  <>
                    <dt className="text-text-muted">Delivered</dt>
                    <dd className="text-text">{formatDateTime(data.delivered_at)}</dd>
                  </>
                )}
                <dt className="text-text-muted">Event id</dt>
                <dd className="flex items-center gap-1 font-mono text-text">
                  <span className="break-all">{data.event_id}</span>
                  <CopyButton value={data.event_id} label="Copy the event id" />
                </dd>
                <dt className="text-text-muted">Event type</dt>
                <dd className="font-mono text-text">{data.event_type}</dd>
              </dl>
              {data.slack_message_url && <a className="text-sm underline" target="_blank" rel="noreferrer" href={data.slack_message_url}>Open message in Slack</a>}
              {data.payload ? (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-medium text-text">
                      The event as it was queued. A template body is rendered over it.
                    </p>
                    <CopyButton value={prettyJson(data.payload)} label="Copy the event" />
                  </div>
                  <pre className="max-h-[50vh] overflow-auto rounded-md border border-border bg-surface-sunken px-3 py-2 font-mono text-2xs text-text">
                    {prettyJson(data.payload)}
                  </pre>
                </div>
              ) : (
                <Callout tone="info">
                  Its event was emptied after the retention period, so there is nothing left to show or to send.
                </Callout>
              )}
            </>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button variant="default" disabled={!canSendAgain} loading={replay.isPending} onClick={() => void sendAgain()}>
            <RotateCcw />
            Send again
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
